/*
 * WHAT THE TAB SAYS — the other half of "the address bar is the share button".
 *
 * `permalink.ts` reflects the open view into the URL so the browser's own copy, bookmark and share
 * work without this app building controls for them. It reflected the address and not the NAME: every
 * view — the front door, Abraham, Isaiah 53, two accounts side by side — left the tab reading
 * "Verses Verse — the scriptures, cross-linked".
 *
 * That is the one string a reader keeps. A bookmark saves it, so ten saved figures are ten identical
 * rows. Browser history stores it, so searching history for "Abraham" finds nothing. A row of open
 * tabs is a row of the same word. And the phone share sheet offers it as the suggested text.
 *
 * The 2,688 static pages get this right — every one of their titles is unique, measured. The app is
 * where a reader actually spends their time and it had one title for everything.
 *
 * NOTHING ON SCREEN CHANGES. This is the quietest thing in the app: it costs no pixel, asks for
 * nothing, and is only ever noticed later, by a reader trying to find their way back.
 */

/**
 * The site's own name, as the shell already writes it in `index.html`.
 *
 * Spelled here rather than read from `document.title` at runtime: by the time the first view opens,
 * the title may already have been rewritten by this module, and a name derived from its own output
 * is a name that drifts one view at a time.
 */
export const SITE_NAME = 'Verses Verse';

/** The shell's title — what the tab reads with nothing open. Matches `web/index.html`. */
export const SHELL_TITLE = `${SITE_NAME} — the scriptures, cross-linked`;

/**
 * The title for a view whose subject is `subject`, or the shell's own when nothing is open.
 *
 * The subject leads. A tab is truncated from the RIGHT to a couple of dozen characters, so a title
 * that opens with the site name is the site name repeated across every tab and nothing else — which
 * is the state this replaces. "Abraham — Verses Verse" survives that cut with the part that
 * distinguishes it intact.
 *
 * A blank or whitespace subject is the same as none: a view that cannot say what it is about should
 * not put an empty dash in the tab.
 */
export function documentTitle(subject: string | null | undefined): string {
  const named = subject?.trim() ?? '';
  return named === '' ? SHELL_TITLE : `${named} — ${SITE_NAME}`;
}
